<script>

    import {
        deliveryStore,
        selectDelivery,
        selectPickup,
        selectPickupPoint,
        orderConfigStore,
        orderFormFieldsStore,
        selectOnsite, selectDeliveryRegion
    } from "./orders-store.js";
    import Button from "../components/Button.svelte";
    import {openCartScreen, screensStore} from "../core/screens-manager.js";
    import {getDeliveryPriceByRegion, submitOrder} from "./orders.js";
    import OptionItem from "../components/OptionItem.svelte";
    import InputLabel from "../components/InputLabel.svelte";
    import {get} from "svelte/store";
    import TotalBlock from "../components/TotalBlock.svelte";
    import AcceptPolicy from "../components/AcceptPolicy.svelte";
    import {privacyPolicyStore} from "../core/docs-store.js";
    import {acceptPolicyStore} from "./accept-policy-store.js";
    import { t } from "../core/i18n/language-store.js";

    const orderConfig = get(orderConfigStore);

    export let publishConfig = {};

    let name;
    let nameIsEmpty;
    let phone;
    let address;
    let deliveryRegion;
    let comment;
    let customFieldValuesMap = {};

    /// ERRORS
    let phoneIsEmpty;
    let phoneIsWrong;
    let deliveryRegionIsEmpty;
    let addressIsEmpty;
    let commentIsEmpty;
    let pickupPointIsEmpty;
    let customFieldErrorsMap = {};

    let deliveryRegionNote = undefined;
    let deliveryRegionPrice = 0;
    let finalDeliveryPrice = 0;

    let submitInProgress = false;

    function selectDeliveryRegionHandler () {
        selectDeliveryRegion(deliveryRegion);

        deliveryRegionNote = (orderConfig.deliveryRegions || []).find(x => x.id === deliveryRegion)?.note;
        deliveryRegionPrice = getDeliveryPriceByRegion(get(deliveryStore), deliveryRegion, orderConfig);
    }

    function closeScreen () {
        openCartScreen();
    }

    async function submitOrderHandler () {

        try {
            submitInProgress = true;
            const result = await submitOrder({
                name,
                phone,
                deliveryRegion,
                address,
                comment,
                customFieldValuesMap
            });

            nameIsEmpty = result?.error?.nameIsEmpty;
            phoneIsEmpty = result?.error?.phoneIsEmpty;
            phoneIsWrong = result?.error?.phoneIsWrong;
            deliveryRegionIsEmpty = result?.error?.deliveryRegionIsEmpty;
            addressIsEmpty = result?.error?.addressIsEmpty;
            commentIsEmpty = result?.error?.commentIsEmpty;
            pickupPointIsEmpty = result?.error?.pickupPointIsEmpty;
            customFieldErrorsMap = result?.error?.customFieldErrorsMap;
        }
        catch (e) {
            console.error(e);
        }
        finally {
            submitInProgress = false;
        }
    }
</script>

<style>

    .order-form-content {
        display: flex;
        flex-direction: column;
        gap: 36px;
    }

    .group {
        display: flex;
        flex-direction: column;
        gap: 24px;
    }

    .order-icon {
        color: var(--accent);
    }
    .order-form {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }
    .prefill-info {
        font-weight: 400;
        line-height: 120%;
        padding-bottom: .5rem;
    }
    .info, .info a, .info a:visited {
        color: var(--order-footer-text-color);
        line-height: 110%;
        font-size: 0.8125rem;
        text-align: center;
    }
    .delivery-info-block {
        font-family: Roboto Condensed, sans-serif;
        padding: .7rem .8rem;
        border-radius: 0;
        background: var(--delivery-info-bg);
        color: var(--main-font-color);
        display: flex;
        gap: .6rem;
        align-items: center;
        line-height: 120%;
        font-weight: 400;
        font-size: var(--fs-85);
    }
    .delivery-info-block .price {
        font-family: PT Mono, sans-serif;
        color: var(--accent-color);
    }
</style>


{#if ($screensStore.orderFormOpened)}

    <button class="screen-close-button pointer" on:click={closeScreen}>
        <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="ai ai-Cross"><path d="M20 20L4 4m16 0L4 20"/></svg>
    </button>

    <div class="desktop-screen layer-cart screen-content">

        <!-- HEADER -->
        <div class="screen-header">
            <h1>{$t('orderScreen.title')}</h1>
        </div>

        <!-- INFO MESSAGE -->
        {#if ($orderConfigStore.preFillInfo)}
            <div class="prefill-info">{ $orderConfigStore.preFillInfo }</div>
            <div class="separator"></div>
        {/if}

        <div class="order-form-content">

            <!-- STEP 1: TYPE SELECTOR -->

            <div class="group">

                <!-- TYPE SELECTOR: ONSITE / DELIVERY / PICKUP -->
                {#if ($deliveryStore.showDeliveryOrPickupSelector)}
                    <div class="input-group">
                        <label>{$t('orderScreen.deliveryType.label')}</label>

                        <div>
                            {#if ($orderConfigStore.onsiteEnabled)}
                                <OptionItem label="{$t('orderScreen.deliveryType.onsite')}"
                                            selected="{$deliveryStore.selectedOnsite}"
                                            on:select={selectOnsite}
                                ></OptionItem>
                            {/if}

                            {#if ($orderConfigStore.deliveryEnabled)}
                                <OptionItem label="{$t('orderScreen.deliveryType.delivery')}"
                                            selected="{$deliveryStore.selectedDelivery}"
                                            on:select={selectDelivery}
                                ></OptionItem>
                            {/if}

                            {#if ($orderConfigStore.pickupEnabled)}
                                <OptionItem label="{$t('orderScreen.deliveryType.pickup')}"
                                            selected="{$deliveryStore.selectedPickup}"
                                            on:select={selectPickup}
                                ></OptionItem>
                            {/if}
                        </div>
                    </div>
                {/if}

            </div>

            <!-- STEP 2: REGION/PICKUP POINT -->

            {#if (($deliveryStore.selectedDelivery && $orderConfigStore.deliveryRegions?.length > 0) || $deliveryStore.selectedPickup)}
                <div class="group fade-up-animation">

                    <div class="separator"></div>

                    <!-- STEP 2 - DELIVERY REGION SELECTOR -->
                    {#if ($deliveryStore.selectedDelivery && $orderConfigStore.deliveryRegions && $orderConfigStore.deliveryRegions.length > 0  )}
                        <div class="input-group">
                            <InputLabel label="{$t('orderScreen.fields.deliveryRegion')}"
                                        required={true}
                                        hasError={deliveryRegionIsEmpty}
                                        requiredError={$t('orderScreen.errors.deliveryRegion')}>
                            </InputLabel>
                            <select id="region" type="text" name="delivery_region" placeholder={$t('orderScreen.placeholders.deliveryRegion')} bind:value={deliveryRegion} on:change={() => selectDeliveryRegionHandler()}>
                                <option value=""></option>
                                {#each ($orderConfigStore.deliveryRegions || []) as region }
                                    <option value={region.id}>{region.name}</option>
                                {/each}
                            </select>
                        </div>

                        {#if (deliveryRegionNote)}
                            <div class="delivery-info-block">
                                <svg width="21" height="25" viewBox="0 0 21 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M10.7617 4.99803C8.78391 4.99803 6.85051 5.58452 5.20602 6.68333C3.56153 7.78214 2.2798 9.34393 1.52293 11.1712C0.766052 12.9985 0.568018 15.0091 0.95387 16.9489C1.33972 18.8887 2.29213 20.6706 3.69065 22.0691C5.08918 23.4676 6.87101 24.42 8.81082 24.8059C10.7506 25.1917 12.7613 24.9937 14.5886 24.2368C16.4158 23.4799 17.9776 22.1982 19.0764 20.5537C20.1752 18.9092 20.7617 16.9758 20.7617 14.998C20.7587 12.3468 19.7041 9.80501 17.8294 7.93031C15.9547 6.0556 13.413 5.00106 10.7617 4.99803ZM10.7617 22.498C9.27836 22.498 7.82831 22.0582 6.59494 21.234C5.36158 20.4099 4.40028 19.2386 3.83263 17.8681C3.26497 16.4977 3.11644 14.9897 3.40583 13.5348C3.69522 12.08 4.40953 10.7436 5.45842 9.69472C6.50731 8.64583 7.84369 7.93152 9.29854 7.64214C10.7534 7.35275 12.2614 7.50127 13.6318 8.06893C15.0023 8.63659 16.1736 9.59788 16.9977 10.8312C17.8219 12.0646 18.2617 13.5147 18.2617 14.998C18.2595 16.9865 17.4686 18.8928 16.0626 20.2989C14.6565 21.7049 12.7502 22.4958 10.7617 22.498ZM14.5628 10.7803C14.6793 10.8964 14.7718 11.0344 14.8348 11.1864C14.8979 11.3383 14.9304 11.5012 14.9304 11.6657C14.9304 11.8303 14.8979 11.9931 14.8348 12.1451C14.7718 12.297 14.6793 12.435 14.5628 12.5511L11.6461 15.4678C11.4113 15.7026 11.0928 15.8346 10.7607 15.8346C10.4286 15.8346 10.1101 15.7026 9.87526 15.4678C9.64044 15.233 9.50851 14.9145 9.50851 14.5824C9.50851 14.2503 9.64044 13.9318 9.87526 13.697L12.7919 10.7803C12.9081 10.6638 13.0461 10.5713 13.198 10.5082C13.3499 10.4451 13.5128 10.4127 13.6773 10.4127C13.8419 10.4127 14.0048 10.4451 14.1567 10.5082C14.3086 10.5713 14.4466 10.6638 14.5628 10.7803ZM7.01172 2.08136C7.01172 1.74984 7.14342 1.4319 7.37784 1.19748C7.61226 0.963056 7.9302 0.83136 8.26172 0.83136H13.2617C13.5932 0.83136 13.9112 0.963056 14.1456 1.19748C14.38 1.4319 14.5117 1.74984 14.5117 2.08136C14.5117 2.41288 14.38 2.73082 14.1456 2.96524C13.9112 3.19966 13.5932 3.33136 13.2617 3.33136H8.26172C7.9302 3.33136 7.61226 3.19966 7.37784 2.96524C7.14342 2.73082 7.01172 2.41288 7.01172 2.08136Z" fill="currentColor"/>
                                </svg>
                                {deliveryRegionNote}
                            </div>
                        {/if}

                        {#if (deliveryRegionPrice)}
                            <div class="delivery-info-block">
                                <svg width="17" height="26" viewBox="0 0 17 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M8.76148 2.73639C8.76148 2.10019 9.01421 1.49004 9.46407 1.04018C9.91393 0.590316 10.5241 0.337585 11.1603 0.337585C11.7965 0.337585 12.4066 0.590316 12.8565 1.04018C13.3064 1.49004 13.5591 2.10019 13.5591 2.73639C13.5591 3.37259 13.3064 3.98274 12.8565 4.4326C12.4066 4.88246 11.7965 5.13519 11.1603 5.13519C10.5241 5.13519 9.91393 4.88246 9.46407 4.4326C9.01421 3.98274 8.76148 3.37259 8.76148 2.73639ZM7.08731 10.2976C7.03734 10.3176 6.99236 10.3376 6.94238 10.3576L6.54258 10.5325C5.72299 10.8973 5.09331 11.592 4.80845 12.4415L4.67851 12.8314C4.39865 13.6709 3.49411 14.1207 2.65452 13.8408C1.81494 13.561 1.36517 12.6564 1.64503 11.8169L1.77496 11.4271C2.34468 9.7229 3.60405 8.3336 5.24323 7.60396L5.64303 7.42905C6.68251 6.96928 7.80695 6.7294 8.94639 6.7294C11.1753 6.7294 13.1843 8.06873 14.0388 10.1227L14.8085 11.9668L15.8779 12.5015C16.6675 12.8963 16.9874 13.8558 16.5926 14.6454C16.1978 15.4351 15.2382 15.7549 14.4486 15.3601L13.1093 14.6954C12.5946 14.4356 12.1898 14.0058 11.9699 13.471L11.4901 12.3216L10.5256 15.595L12.9994 18.2936C13.2692 18.5885 13.4591 18.9433 13.5591 19.3331L14.7085 23.9358C14.9234 24.7904 14.4037 25.66 13.5441 25.8748C12.6845 26.0897 11.82 25.57 11.6051 24.7104L10.5056 20.3076L6.97237 16.4545C6.23274 15.6499 5.95788 14.5255 6.23774 13.471L7.08232 10.2976H7.08731ZM4.19875 20.2277L5.44813 17.1092C5.55308 17.2591 5.67302 17.3991 5.79796 17.539L7.83194 19.7579L7.1073 21.567C6.98736 21.8668 6.80745 22.1417 6.57757 22.3716L3.49411 25.4551C2.86942 26.0797 1.85492 26.0797 1.23024 25.4551C0.605547 24.8304 0.605547 23.8159 1.23024 23.1912L4.19875 20.2277Z" fill="currentColor"/>
                                </svg>
                                <span>
                                    <span>{$t('orderScreen.deliveryInfo.cost')}</span>
                                    <span class="price nowrapprice-pair" class:price-pair--rev={publishConfig?.currency === '$'}>
                                        <span>{deliveryRegionPrice}</span>
                                        <span>{publishConfig.currency || ''}</span>
                                    </span>
                                </span>
                            </div>
                        {/if}
                    {/if}

                    <!-- STEP 2 - PICKUP POINT SELECTOR -->
                    {#if ($orderConfigStore.pickupEnabled && $deliveryStore.selectedPickup && $orderConfigStore.pickupPoints)}
                        <div class="input-group">

                            <InputLabel label={$t('orderScreen.fields.pickupPoint')}
                                        required={$deliveryStore.selectedPickup}
                                        hasError={pickupPointIsEmpty}
                                        requiredError={$t('orderScreen.errors.pickupPoint')}>
                            </InputLabel>

                            <div>
                                {#each ($orderConfigStore.pickupPoints || []) as point}
                                    <OptionItem label={ point.address }
                                                note={ point.note }
                                                selected={point === $deliveryStore.selectedPickupPoint}
                                                disabled={point.enabled === false}
                                                on:select={() => selectPickupPoint(point)}
                                    ></OptionItem>
                                {/each}
                            </div>
                        </div>
                    {/if}

                </div>
            {/if}

            <!-- STEP 3: DETAILS -->
            {#if ($deliveryStore.selectedOnsite || $deliveryStore.selectedDelivery || $deliveryStore.selectedPickupPoint)}

                <!-- FIELDS -->

                <div class="group fade-up-animation">

                    <div class="separator"></div>

                    <!-- ADDRESS FIELD-->
                    {#if ($orderFormFieldsStore.showAddressField && $deliveryStore.selectedDelivery)}
                        <div class="input-group">
                            <InputLabel label={$t('orderScreen.fields.address')} 
                                      required={$orderFormFieldsStore.requireAddressField} 
                                      hasError={addressIsEmpty} 
                                      requiredError={$t('orderScreen.errors.address')}>
                            </InputLabel>
                            <input id="address" type="text" name="address" placeholder={$t('orderScreen.placeholders.address')} bind:value={address}>
                        </div>
                    {/if}

                    <!-- NAME -->
                    {#if ($orderFormFieldsStore.showNameField && !$deliveryStore.selectedOnsite)}
                        <div class="input-group">
                            <InputLabel label={$t('orderScreen.fields.name')} 
                                      required={$orderFormFieldsStore.requireNameField} 
                                      hasError={nameIsEmpty} 
                                      requiredError={$t('orderScreen.errors.name')}>
                            </InputLabel>
                            <input type="text" id="name" name="name" placeholder={$t('orderScreen.placeholders.name')} bind:value={name}>
                        </div>
                    {/if}

                    <!-- PHONE -->
                    {#if ($orderFormFieldsStore.showPhoneField && !$deliveryStore.selectedOnsite)}
                        <div class="input-group">
                            <InputLabel label={$t('orderScreen.fields.phone')} 
                                      required={$orderFormFieldsStore.requirePhoneField} 
                                      hasError={phoneIsEmpty || phoneIsWrong} 
                                      requiredError={phoneIsEmpty ? $t('orderScreen.errors.phone.empty') : (phoneIsWrong ? $t('orderScreen.errors.phone.invalid') : '')}>
                            </InputLabel>
                            <input id="phone" type="text" name="phone" placeholder={$t('orderScreen.placeholders.phone')} bind:value={phone}>
                        </div>
                    {/if}

                    <!-- COMMENTS -->
                    {#if ($orderFormFieldsStore.showCommentField)}
                        <div class="input-group">
                            <InputLabel label={$t('orderScreen.fields.comment')} 
                                      required={$orderFormFieldsStore.requireCommentField} 
                                      hasError={commentIsEmpty} 
                                      requiredError={$t('orderScreen.errors.comment')}>
                            </InputLabel>
                            <textarea id="comment" name="comment" rows="4" placeholder={$t('orderScreen.placeholders.comment')} bind:value={comment}></textarea>
                        </div>
                    {/if}

                    <!-- CUSTOM FIELDS -->
                    {#each ($orderConfigStore.customFields || []) as customField }
                        {#if (customField.hide !== true)}
                            <div class="input-group">
                        <InputLabel label="{ customField.name }"
                                    required={customField.required}
                                    hasError="{customFieldErrorsMap && customFieldErrorsMap[customField.key]}"
                                    requiredError="{$t('orderScreen.errors.required')}">
                        </InputLabel>
                                <input type="text" placeholder="" bind:value={customFieldValuesMap[customField.key]}>
                            </div>
                        {/if}
                    {/each}

                </div>


                <!-- TOTALS AND PLACE ORDER BUTTON -->

                <div class="screen-footer">

                    <div class="separator"></div>

                    <TotalBlock></TotalBlock>

                    <div class="separator"></div>

                    {#if ($orderConfigStore.showUserAgreement || $orderConfigStore.showPrivacyPolicy)}
                        <div class="info">
                            <span>{$t('orderScreen.agreement.text')} </span>
                            {#if ($orderConfigStore.showUserAgreement)}<a href="./user-agreement.html">{$t('orderScreen.agreement.userAgreement')}</a>{/if}
                            {#if ($orderConfigStore.showUserAgreement && $orderConfigStore.showPrivacyPolicy)}{$t('orderScreen.agreement.and')}{/if}
                            {#if ($orderConfigStore.showPrivacyPolicy)}<a href="./privacy-policy.html">{$t('orderScreen.agreement.privacyPolicy')}</a>{/if}
                        </div>
                    {/if}

                    {#if (!$acceptPolicyStore.hideAcceptPolicy) }
                        <AcceptPolicy></AcceptPolicy>
                    {/if}

                    <Button icon="bag"
                            type="primary"
                            label="{$orderConfigStore.placeOrderButtonText || $t('cartOrder.placeOrder')}"
                            disabled={!($privacyPolicyStore.accepted || $acceptPolicyStore.hideAcceptPolicy) || submitInProgress || !($deliveryStore.selectedOnsite || deliveryRegion || $deliveryStore.selectedPickupPoint)}
                            running={submitInProgress}
                            on:clicked={submitOrderHandler}>
                    </Button>
                </div>
            {/if}

        </div>
    </div>
{/if}
